<div class="space-y-6">
  <app-page-header
    [backTo]="['/my-invoices']"
    [backLabel]="'invoices.retourAMesFactures' | t"
  >
    @if (invoice(); as inv) {
      <div slot="actions" class="flex flex-wrap items-center gap-2">
          <!-- Toujours via /download : le back y régénère le PDF s'il manque (#98) ou s'il est
               périmé (statut payée / annulée / frais de retard — 2026-10-23). Ne jamais lier
               `pdfUrl` directement : c'est un instantané qui peut être en retard d'un statut. -->
          <button
            type="button"
            (click)="downloadPdf()"
            [disabled]="pdfBusy()"
            class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            @if (pdfBusy()) {
              <svg class="size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path></svg>
              {{ 'invoices.generatingPdf' | t }}
            } @else {
              {{ 'invoices.telechargerPdf' | t }}
            }
          </button>
      </div>
    }
  </app-page-header>

  @if (pdfError()) {
    <div class="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ pdfError() }}
    </div>
  }

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      [loadingMessage]="'invoices.chargementDeLaFacture' | t"
      (retry)="fetch()"
    />
  } @else if (invoice(); as inv) {
    <!-- Header card -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="space-y-2">
          <div class="flex flex-wrap items-center gap-3">
            <h1 class="text-xl font-semibold text-gray-800 dark:text-white/90">{{ inv.invoiceNumber }}</h1>
            <span
              class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
              [class.bg-brand-50]="statusMeta[inv.status].palette === 'brand'"
              [class.text-brand-700]="statusMeta[inv.status].palette === 'brand'"
              [class.dark:bg-brand-500/15]="statusMeta[inv.status].palette === 'brand'"
              [class.dark:text-brand-400]="statusMeta[inv.status].palette === 'brand'"
              [class.bg-blue-light-50]="statusMeta[inv.status].palette === 'blue-light'"
              [class.text-blue-light-700]="statusMeta[inv.status].palette === 'blue-light'"
              [class.dark:bg-blue-light-500/15]="statusMeta[inv.status].palette === 'blue-light'"
              [class.dark:text-blue-light-400]="statusMeta[inv.status].palette === 'blue-light'"
              [class.bg-warning-50]="statusMeta[inv.status].palette === 'warning'"
              [class.text-warning-700]="statusMeta[inv.status].palette === 'warning'"
              [class.dark:bg-warning-500/15]="statusMeta[inv.status].palette === 'warning'"
              [class.dark:text-warning-400]="statusMeta[inv.status].palette === 'warning'"
              [class.bg-gray-100]="statusMeta[inv.status].palette === 'gray'"
              [class.text-gray-700]="statusMeta[inv.status].palette === 'gray'"
              [class.dark:bg-gray-800]="statusMeta[inv.status].palette === 'gray'"
              [class.dark:text-gray-300]="statusMeta[inv.status].palette === 'gray'"
              [class.bg-error-50]="statusMeta[inv.status].palette === 'error'"
              [class.text-error-700]="statusMeta[inv.status].palette === 'error'"
              [class.dark:bg-error-500/15]="statusMeta[inv.status].palette === 'error'"
              [class.dark:text-error-400]="statusMeta[inv.status].palette === 'error'"
            >
              {{ statusMeta[inv.status].label | t }}
            </span>
            @if (isOverdue() && inv.status !== 'OVERDUE') {
              <span class="inline-flex items-center gap-1 rounded-full bg-error-50 px-2 py-0.5 text-xs font-medium text-error-700 dark:bg-error-500/10 dark:text-error-400">
                {{ 'invoices.echeanceDepassee' | t }}
              </span>
            }
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            @if (inv.rental; as r) {
              <a [routerLink]="['/my-rentals', r.id]" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">
                Location #{{ r.id }}
              </a>
              @if (r.vehicle?.licencePlate) {
                <span class="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
                <span class="font-mono">{{ r.vehicle?.licencePlate }}</span>
              }
            } @else {
              <span class="text-gray-400">{{ 'invoices.locationNonRenseignee' | t }}</span>
            }
          </p>
        </div>
        <div class="text-right">
          <p class="text-xs text-gray-500 dark:text-gray-400">{{ 'invoices.totalAPayer' | t }}</p>
          <p class="text-2xl font-bold text-gray-800 dark:text-white/90">
            {{ (inv.totalAmount ?? 0) | number:'1.0-0' }} <span class="text-sm font-normal text-gray-400">XOF</span>
          </p>
        </div>
      </div>
    </section>

    <div class="grid gap-6 lg:grid-cols-3">
      <!-- Dates -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Dates</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.emiseLe' | t }}</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ inv.issueDate | ldate: 'dd/MM/yyyy' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.echeance' | t }}</dt>
            <dd class="text-right" [class.text-error-700]="isOverdue()" [class.dark:text-error-400]="isOverdue()" [class.text-gray-800]="!isOverdue()" [class.dark:text-white/90]="!isOverdue()">
              {{ inv.dueDate | ldate: 'dd/MM/yyyy' }}
            </dd>
          </div>
          @if (inv.paidDate) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.payeeLe' | t }}</dt>
              <dd class="text-right text-brand-700 dark:text-brand-400">{{ inv.paidDate | ldate: 'dd/MM/yyyy HH:mm' }}</dd>
            </div>
          }
        </dl>
      </section>

      <!-- Parties -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Parties</h2>
        <div class="mt-4 space-y-4">
          <div>
            <p class="mb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Locataire</p>
            <!-- noAutoLink : pas de route /users dans l'app owner (fork admin). -->
            <app-user-cell [user]="inv.renterUser" [fallbackId]="inv.renterId" size="md" [noAutoLink]="true" />
          </div>
          <div class="border-t border-gray-100 pt-4 dark:border-gray-800">
            <p class="mb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'invoices.proprietaire' | t }}</p>
            <app-user-cell [user]="inv.ownerUser" [fallbackId]="inv.ownerId" size="md" [noAutoLink]="true" />
          </div>
        </div>
      </section>

      <!-- Paiement -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'invoices.paiement' | t }}</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.methode' | t }}</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ inv.paymentMethod || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.reference' | t }}</dt>
            <dd class="text-right font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{{ inv.paymentReference || '—' }}</dd>
          </div>
        </dl>
      </section>
    </div>

    <!-- Détail financier -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'invoices.detailDuCalcul' | t }}</h2>
      <dl class="mt-4 grid gap-2 text-sm">
        <div class="flex justify-between gap-4 py-1.5">
          <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.montantLocation' | t }}</dt>
          <dd class="text-right text-gray-800 dark:text-white/90">{{ (inv.rentalAmount ?? 0) | number:'1.0-0' }} XOF</dd>
        </div>
        @if ((inv.lateFee ?? 0) > 0) {
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.fraisDeRetard' | t }}</dt>
            <dd class="text-right text-error-700 dark:text-error-400">+ {{ (inv.lateFee ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
        }
        @if ((inv.additionalCharges ?? 0) > 0) {
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">Frais additionnels</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">+ {{ (inv.additionalCharges ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
        }
        @if ((inv.discount ?? 0) > 0) {
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">Remise</dt>
            <dd class="text-right text-brand-700 dark:text-brand-400">− {{ (inv.discount ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
        }
        <div class="flex justify-between gap-4 border-t border-gray-200 pt-2 dark:border-gray-800">
          <dt class="font-medium text-gray-700 dark:text-gray-300">Sous-total</dt>
          <dd class="text-right font-medium text-gray-800 dark:text-white/90">{{ (inv.subtotal ?? 0) | number:'1.0-0' }} XOF</dd>
        </div>
        @if ((inv.taxAmount ?? 0) > 0) {
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">Taxes</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">+ {{ (inv.taxAmount ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
        }
        <div class="flex justify-between gap-4 border-t border-gray-200 pt-2 dark:border-gray-800">
          <dt class="text-base font-semibold text-gray-800 dark:text-white/90">Total</dt>
          <dd class="text-right text-base font-semibold text-gray-800 dark:text-white/90">{{ (inv.totalAmount ?? 0) | number:'1.0-0' }} XOF</dd>
        </div>
      </dl>
    </section>

    <!-- Caution / dépôt de garantie (#46) -->
    @if ((inv.depositAmount ?? 0) > 0 || (inv.depositRatePct ?? 0) > 0) {
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'invoices.caution' | t }}</h2>
        <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{{ 'invoices.factureeEnSusDe' | t }}</p>
        <dl class="mt-4 grid gap-2 text-sm">
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.depositCharged' | t }} @if ((inv.depositRatePct ?? 0) > 0) { ({{ inv.depositRatePct }} %) }</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ (inv.depositAmount ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
          @if ((inv.amountToCollect ?? 0) > 0) {
            <div class="flex justify-between gap-4 py-1.5">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.totalDebiteAuClient' | t }}</dt>
              <dd class="text-right font-medium text-gray-800 dark:text-white/90">{{ (inv.amountToCollect ?? 0) | number:'1.0-0' }} XOF</dd>
            </div>
          }
          @if ((inv.depositRetained ?? 0) > 0) {
            <div class="flex justify-between gap-4 py-1.5">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.retenueSurCautionFrais' | t }}</dt>
              <dd class="text-right text-error-700 dark:text-error-400">− {{ (inv.depositRetained ?? 0) | number:'1.0-0' }} XOF</dd>
            </div>
          }
          @if ((inv.depositRefund ?? 0) > 0) {
            <div class="flex justify-between gap-4 border-t border-gray-200 pt-2 dark:border-gray-800">
              <dt class="font-medium text-gray-700 dark:text-gray-300">{{ 'invoices.reliquatARembourserAu' | t }}</dt>
              <dd class="text-right font-semibold text-brand-700 dark:text-brand-400">{{ (inv.depositRefund ?? 0) | number:'1.0-0' }} XOF</dd>
            </div>
          }
        </dl>

        @if (declareDone()) {
          <div class="mt-4 rounded-lg border border-success-200 bg-success-50 px-3 py-2 text-sm text-success-700 dark:border-success-800 dark:bg-success-500/10 dark:text-success-400">
            {{ 'invoices.remboursementDeCautionDeclare' | t }}
          </div>
        } @else if ((inv.depositRefund ?? 0) > 0) {
          @if (declareError()) {
            <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
              {{ declareError() }}
            </div>
          }
          @if (inv.refundRequestId) {
            <div class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
              <p class="text-sm text-gray-600 dark:text-gray-400">{{ 'invoices.cautionEncaisseeEnEspeces' | t }}</p>
              <button type="button" (click)="declareCashRefund()" [disabled]="declaring()"
                class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
                @if (declaring()) {
                  <svg class="size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path></svg>
                  {{ 'invoices.saving' | t }}
                } @else { {{ 'invoices.iRefundedDeposit' | t }} }
              </button>
            </div>
          } @else {
            <div class="mt-4 rounded-lg border border-blue-light-200 bg-blue-light-50 px-4 py-3 text-sm text-blue-light-700 dark:border-blue-light-500/30 dark:bg-blue-light-500/10 dark:text-blue-light-400">
              {{ 'invoices.remboursementDuReliquatDe' | t }}
            </div>
          }
        }
      </section>
    }

    <!-- Transactions liées, relayées par rental (§2.8.bis). Pas de lien vers
         /my-transactions : ces transactions appartiennent au PAYEUR (le client),
         elles n'apparaissent pas dans les transactions de l'owner (#99). -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'invoices.transactionsLiees' | t }}</h2>
      @if (linkedTxsLoading()) {
        <p class="mt-3 text-xs text-gray-400">{{ 'invoices.chargement' | t }}</p>
      } @else if (linkedTxs().length === 0) {
        <p class="mt-3 text-sm text-gray-400">{{ 'invoices.aucuneTentativeDePaiement' | t }}</p>
      } @else {
        <ul class="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
          @for (t of linkedTxs(); track t.id) {
            <li class="flex items-center justify-between gap-4 py-3">
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-gray-800 dark:text-white/90">
                  {{ t._metadataTypeLabel || 'Paiement' }}
                  @if (t.numTransaction) {
                    <span class="ml-1.5 font-mono text-xs font-normal text-gray-400">{{ t.numTransaction }}</span>
                  }
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ t.serviceCode || '—' }} · {{ t.createdAt | ldate: 'dd/MM/yy HH:mm' }}
                </p>
              </div>
              <div class="flex items-center gap-3">
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                  [class.bg-brand-50]="txStatusMeta[t.transactionStatus].palette === 'brand'"
                  [class.text-brand-700]="txStatusMeta[t.transactionStatus].palette === 'brand'"
                  [class.bg-warning-50]="txStatusMeta[t.transactionStatus].palette === 'warning'"
                  [class.text-warning-700]="txStatusMeta[t.transactionStatus].palette === 'warning'"
                  [class.bg-gray-100]="txStatusMeta[t.transactionStatus].palette === 'gray'"
                  [class.text-gray-700]="txStatusMeta[t.transactionStatus].palette === 'gray'"
                  [class.bg-error-50]="txStatusMeta[t.transactionStatus].palette === 'error'"
                  [class.text-error-700]="txStatusMeta[t.transactionStatus].palette === 'error'"
                  [class.bg-blue-light-50]="txStatusMeta[t.transactionStatus].palette === 'blue-light'"
                  [class.text-blue-light-700]="txStatusMeta[t.transactionStatus].palette === 'blue-light'"
                >
                  {{ txStatusMeta[t.transactionStatus].label | t }}
                </span>
                <span class="whitespace-nowrap text-sm font-medium text-gray-800 dark:text-white/90">
                  {{ t.amount | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">{{ t.currency }}</span>
                </span>
              </div>
            </li>
          }
        </ul>
      }
    </section>

    @if (inv.notes) {
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Notes</h2>
        <p class="mt-3 whitespace-pre-line text-sm text-gray-700 dark:text-gray-300">{{ inv.notes }}</p>
      </section>
    }

    <!-- Métadonnées -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'invoices.metadonnees' | t }}</h2>
      <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.creeeLe' | t }}</dt>
          <dd class="text-right text-gray-800 dark:text-white/90">{{ inv.createdAt | ldate: 'dd/MM/yyyy HH:mm' }}</dd>
        </div>
        @if (inv.updatedAt) {
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'invoices.miseAJour' | t }}</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ inv.updatedAt | ldate: 'dd/MM/yyyy HH:mm' }}</dd>
          </div>
        }
      </dl>
    </section>
  }
</div>

